import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import SEO from "../../common/SEO";
import { baseUrl } from "../../config";
import apiService from "../../ApiService";
import { trackEvent } from "../../components/trackingUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faClock,
  faBookOpen,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Reveal from "../../components/Reveal";
const dummyImageUrl = "https://via.placeholder.com/400x200?text=Dummy+Image";

const BlogPost = ({ SEOUrl, ...props }) => {
  const history = useHistory();
  const [filteredPost, setFilteredPost] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1); // State to store total pages

  useEffect(() => {
    trackEvent("PageLoad", "", "BlogPost");
    const fetchFilteredPosts = async () => {
      setLoading(true);
      try {
        const data = await apiService.fetchBlogPostByCategory(SEOUrl, page);
        setTotalPages(data.totalPages); // Update total pages from API response
        if (data.posts && data.posts.length === 0) {
          setHasMore(false); // No more items, stop pagination
          if (page === 1) {
            setError("No items found."); // Show error if no items found on the first page
          }
        } else {
          if (page === 1) {
            setFilteredPost(data.posts);
          } else {
            setFilteredPost((prevPosts) => [...prevPosts, ...data.posts]);
          }
          setError(null); // Clear any existing error
        }
      } catch (error) {
        console.error("Error fetching filtered blog posts:", error);
        if (error.response && error.response.status === 404) {
          setHasMore(false); // No more items, stop pagination
          if (page === 1) {
            setError("No items found."); // Show error if no items found on the first page
          }
        } else {
          setError(`${error.message}`); // Set generic error message for other errors
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredPosts();
  }, [SEOUrl, page]);

  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const convertToTitleCase = (text) => {
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between lowercase and uppercase letters
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2") // Add space between uppercase letters and lowercase letters
      .replace(/-/g, " ") // Replace hyphens with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  // Extracting keywords and description from the first post if available
  const firstPost = filteredPost.length > 0 ? filteredPost[0] : null;
  const categoryTitle = convertToTitleCase(SEOUrl);

  // Enhanced SEO data
  const seoKeywords = firstPost
    ? firstPost.keywords
    : `${categoryTitle}, ${categoryTitle} articles, ${categoryTitle} tutorials, ${categoryTitle} guides, ${categoryTitle} tips, ${categoryTitle} best practices, GeniusNexa blog, tech articles, technology insights, software development, coding tutorials`;

  const seoDescription = firstPost
    ? firstPost.description
    : `Explore ${
        filteredPost.length || "expert"
      } articles on ${categoryTitle} at GeniusNexa. Get in-depth tutorials, guides, tips, and insights on ${categoryTitle.toLowerCase()} from industry professionals. Stay updated with the latest ${categoryTitle.toLowerCase()} trends and best practices in 2025.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* JSON-LD Structured Data for Category Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${categoryTitle} Articles - GeniusNexa Blog`,
          description: seoDescription,
          url: `${baseUrl}/blogs/${SEOUrl}`,
          publisher: {
            "@type": "Organization",
            name: "GeniusNexa",
            url: baseUrl,
            logo: {
              "@type": "ImageObject",
              url: `${baseUrl}/images/logo.png`,
            },
          },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: filteredPost.slice(0, 10).map((post, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "BlogPosting",
                headline: post.Title,
                description: post.Description,
                url: `${baseUrl}/blogs/${SEOUrl}/${post.SEOUrl}`,
                datePublished: post.CreatedDate,
                dateModified: post.ModifiedDate,
                author: {
                  "@type": "Person",
                  name: post.Author || "GeniusNexa Team",
                },
                image: post.ImageUrl
                  ? `${baseUrl}${post.ImageUrl}`
                  : `${baseUrl}/images/default-blog.jpg`,
                publisher: {
                  "@type": "Organization",
                  name: "GeniusNexa",
                  logo: {
                    "@type": "ImageObject",
                    url: `${baseUrl}/images/logo.png`,
                  },
                },
              },
            })),
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: baseUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Blog",
                item: `${baseUrl}/blogs`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: categoryTitle,
                item: `${baseUrl}/blogs/${SEOUrl}`,
              },
            ],
          },
        })}
      </script>

      <SEO
        title={`${categoryTitle} Articles & Guides 2025 - GeniusNexa Blog`}
        description={seoDescription}
        keywords={seoKeywords}
        image={
          firstPost && firstPost.ImageUrl
            ? baseUrl + firstPost.ImageUrl
            : baseUrl + "/images/blog-category.jpg"
        }
        url={baseUrl + "/blogs/" + SEOUrl}
      />

      {/* Header Section */}
      <header className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="category-pattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="15" cy="15" r="2" fill="currentColor" />
                <circle cx="5" cy="5" r="1" fill="currentColor" opacity="0.5" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#category-pattern)" />
          </svg>
        </div>

        <div className="relative z-10 container mx-auto px-4">
          {/* Breadcrumb */}
          <button
            onClick={() => history.push("/blogs")}
            className="flex items-center gap-2 text-gray-300 hover:text-[#E5C558] transition-colors mb-6 group"
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="group-hover:-translate-x-1 transition-transform"
            />
            <span>Back to All Articles</span>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#E5C558] rounded-xl flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBookOpen}
                className="text-gray-900 text-xl"
              />
            </div>
            <div>
              <p className="text-gray-400 text-sm font-medium">Category</p>
              <h1 className="text-4xl md:text-5xl font-bold">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {convertToTitleCase(SEOUrl)}
                </span>
              </h1>
            </div>
          </div>

          {!loading && filteredPost.length > 0 && (
            <p className="text-gray-300 text-lg">
              {filteredPost.length}{" "}
              {filteredPost.length === 1 ? "article" : "articles"} in this
              category
            </p>
          )}
        </div>
      </header>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-16">
        {error && (
          <Reveal>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-6 py-4 rounded-2xl mb-8 text-center shadow-lg">
              <p className="font-medium">{error}</p>
            </div>
          </Reveal>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {filteredPost &&
            filteredPost.map((post, idx) => (
              <Reveal key={idx} delay={0.1 * (idx % 8)}>
                <Link
                  to={`/blogs/${SEOUrl}/${post.SEOUrl}`}
                  onClick={() =>
                    trackEvent("Blog", `View_${post.Title}`, "CategoryPage")
                  }
                  className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden h-56 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600">
                    <img
                      src={
                        post.ImageUrl ? baseUrl + post.ImageUrl : dummyImageUrl
                      }
                      alt={post.Title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = dummyImageUrl;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-[#E5C558] dark:group-hover:text-[#E5C558] transition-colors line-clamp-2 min-h-[3.5rem]">
                      {post.Title}
                    </h3>

                    {post.Description && (
                      <p className="text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 flex-grow">
                        {post.Description}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                      {post.Author && (
                        <span className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#E5C558] flex items-center justify-center text-gray-900 text-xs font-bold">
                            {post.Author.charAt(0).toUpperCase()}
                          </div>
                          {post.Author}
                        </span>
                      )}
                      {post.ModifiedDate && (
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faClock} className="text-xs" />
                          {new Date(post.ModifiedDate).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center text-[#E5C558] font-semibold group-hover:gap-3 transition-all">
                      Read Article
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 group-hover:translate-x-2 transition-transform duration-300"
                      />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
        </div>

        {loading && (
          <div className="text-center mt-12">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-[#E5C558] text-4xl animate-spin"
            />
            <p className="text-gray-600 dark:text-gray-400 mt-4">
              Loading more articles...
            </p>
          </div>
        )}

        {page < totalPages && hasMore && !loading && !error && (
          <Reveal>
            <div className="text-center mt-12">
              <button
                className="px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-gray-900 font-bold rounded-xl hover:shadow-xl hover:shadow-[#E5C558]/30 hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto"
                onClick={handleLoadMore}
              >
                Load More Articles
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <p className="text-gray-500 dark:text-gray-400 mt-4">
                Page {page} of {totalPages}
              </p>
            </div>
          </Reveal>
        )}

        {!loading && !error && filteredPost.length === 0 && (
          <Reveal>
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <svg
                  className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600 mb-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-3xl font-bold text-gray-700 dark:text-gray-300 mb-3">
                  No articles yet
                </h3>
                <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                  Check back later for new content in this category
                </p>
                <button
                  onClick={() => history.push("/blogs")}
                  className="px-6 py-3 bg-[#E5C558] text-gray-900 font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Browse All Articles
                </button>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
